import me from "models/me";
import siteConfig from "site-config";
import { mutateAsync as _mutateAsync, requestAsync as _requestAsync } from "redux-query";
import { normalize } from "normalizr";
import { getStore } from "store";

const recordCache = {};

export const checkForLogout = (response) => {
    if (response.status === 401) {
        me.logout(true);
        throw Error("Unauthorized");
    }
    return response;
};

export const getModelForSchema = (schemaId, builder) => {
    // const { schema, schemas } = getStore().getState();
    const schemas = getStore().getState().entities.schemas;
    let Record = recordCache[schemaId];
    if (!Record) {
        // build Record
        const schema = schemas[schemaId];
        if (!schema) {
            // this should never happen
            throw new Error(`Schema ${schemaId} not found`);
        }

        Record = builder.create ? builder.create(schema) : builder(schema);
        recordCache[schemaId] = Record;
    }
    return Record;
};

export const getRecordsFromSchema = (schema, records = {}) => {
    if (schema.record) {
        records[schema.key] = schema.record;
    }
    if (schema.schema) {
        if (schema.schema._key) {
            getRecordsFromSchema(schema.schema, records);
        } else {
            Object.keys(schema.schema).forEach((key) => {
                getRecordsFromSchema(schema.schema[key], records);
            });
        }
    }
    return records;
};

const defaultUpdateFunction = (prevEntities, nextEntities) => {
    return {
        ...prevEntities,
        ...nextEntities,
    };
};

export const normalizeData = (response, schema, records?: any) => {
    records = records || getRecordsFromSchema(schema);
    const { scope } = getStore().getState();
    const data = normalize(response.data || response, schema);

    Object.keys(records).forEach((key) => {
        const recordOrBuilder = records[key];
        let Record = recordOrBuilder;
        const collection = data.entities[key];
        if (collection) {
            const keys = Object.keys(collection);
            if (keys.length) {
                // FDM
                if (!recordOrBuilder.prototype.toJS) {
                    if (!scope.brand || !scope.brand._id) {
                        return;
                    }
                    // check if not Immutable Record
                    const schemaId = collection[keys[0]].__schema_id;
                    Record = getModelForSchema(schemaId, recordOrBuilder);
                }
                keys.forEach((id) => {
                    collection[id] = new Record(collection[id]);
                });
            }
        }
    });

    return data;
};

export const requestAsync = ({ doNotMutateUrl = false, ...config }) => {
    if (!doNotMutateUrl && config.url.indexOf("http") !== 0 && config.url.indexOf(siteConfig.goAPI + "/v1/") !== 0) {
        config.url = siteConfig.api + config.url;
    }

    const updateFunctions = {};

    let records;

    if (config.schema) {
        records = getRecordsFromSchema(config.schema);

        Object.keys(records).forEach((key) => {
            updateFunctions[key] = defaultUpdateFunction;
        });
    }

    const { scope } = getStore().getState();

    const activeBrand = me.isSignedIn() && scope.brand ? scope.brand._id || "" : "";

    const optionsMixins = {
        credentials: "include",
        headers: {
            Accept: "application/json",
            "Active-Brand": config.brandId ? config.brandId : activeBrand,
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            module: "default",
        },
        ...(config.options || {}),
    };

    return _requestAsync({
        force: true,
        transform: (response) => {
            if (!records) {
                return response;
            }

            if (!response) {
                return { _result: [] };
            }

            const data = normalizeData(response, config.schema, records);

            return { ...data.entities, _result: data.result };
        },
        update: updateFunctions,
        ...config,
        options: optionsMixins,
    });
};

export const mutateAsync = ({ doNotMutateUrl = false, options, ...config }) => {
    if (!doNotMutateUrl && config.url.indexOf("http") !== 0 && config.url.indexOf(siteConfig.goAPI + "/v1/") !== 0) {
        config.url = siteConfig.api + config.url;
    }

    const updateFunctions = {};

    let records;

    const { scope } = getStore().getState();

    if (config.schema) {
        records = getRecordsFromSchema(config.schema);

        Object.keys(records).forEach((key) => {
            updateFunctions[key] = defaultUpdateFunction;
        });
    }

    const optionsMixins = {
        credentials: "include",
        headers: {
            Accept: "application/json",
            "Active-Brand": me.isSignedIn() && scope.brand ? scope.brand._id || "" : "",
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            module: "default",
        },
        ...(options || {}),
    };

    return _mutateAsync({
        force: true,
        transform: (response) => {
            if (!records) {
                return response;
            }

            if (!response) {
                return { _result: [] };
            }

            const data = normalizeData(response, config.schema, records);
            return { ...data.entities, _result: data.result };
        },
        update: updateFunctions,
        ...config,
        options: optionsMixins,
    });
};
